import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { IndianRupee, Receipt, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const INR = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

/**
 * AccountingTaxes — dedicated taxes view with GST + TCS sub-tabs.
 * Every GST or TCS figure computed/recorded in the system rolls up here so
 * the user can see tax liabilities in ONE place, separate from revenue.
 *
 * GST tab:
 *   - GST Collected (from clients) — from confirmed trips
 *   - GST Paid (to vendors) — from expenses
 *   - Net GST Liability
 *
 * TCS tab:
 *   - TCS Collected (from clients) — auto-calculated on international trips
 *   - TCS Paid (to vendors) — from expenses
 */
const AccountingTaxes = () => {
  const [report, setReport] = useState(null);
  const [tcsBreakdown, setTcsBreakdown] = useState({ collected: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('gst');

  useEffect(() => {
    const load = async () => {
      try {
        const [summary, trips, invoices] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/accounting/reports/summary`),
          axios.get(`${BACKEND_URL}/api/trips`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/invoices`).catch(() => ({ data: [] })),
        ]);
        setReport(summary.data);
        // A trip whose invoice was cancelled must drop out of every tax
        // calculation, same as the reconciliation report already does --
        // this trip-level roll-up only checked trip.status, so a cancelled
        // invoice on an otherwise-confirmed trip still got counted.
        const cancelledTripIds = new Set(
          (invoices.data || [])
            .filter((i) => (i.status || '').toLowerCase() === 'cancelled')
            .flatMap((i) => [i.tripId, i.displayTripId].filter(Boolean))
        );
        // Roll up TCS collected from confirmed trips (international, 2% default)
        const confirmedStatuses = ['confirmed', 'accepted', 'completed'];
        const tcsData = (trips.data || []).reduce((acc, t) => {
          if (!confirmedStatuses.includes((t.status || '').toLowerCase())) return acc;
          if (cancelledTripIds.has(t.tripId) || cancelledTripIds.has(t.displayTripId)) return acc;
          const tcs = Number(t.tcsAmount || 0);
          if (tcs > 0) { acc.collected += tcs; acc.count += 1; }
          return acc;
        }, { collected: 0, count: 0 });
        setTcsBreakdown(tcsData);
      } catch (e) {
        console.error('Failed to load taxes data', e);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }
  if (!report) return <p className="text-center text-slate-500 py-16">Failed to load taxes data</p>;

  const { gstCollected, gstPaid, tcsPaid } = report;
  const netGst = (gstCollected || 0) - (gstPaid || 0);

  return (
    <div className="space-y-6" data-testid="taxes-tab">
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-slate-100/80 p-1 rounded-xl" data-testid="taxes-sub-tabs">
          <TabsTrigger value="gst" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-gst">
            <Receipt className="h-4 w-4" />GST
          </TabsTrigger>
          <TabsTrigger value="tcs" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2" data-testid="tab-tcs">
            <FileText className="h-4 w-4" />TCS
          </TabsTrigger>
        </TabsList>

        {/* GST sub-tab */}
        <TabsContent value="gst" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="p-5 bg-emerald-50/50 border border-emerald-100" data-testid="gst-collected-card">
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee className="h-4 w-4 text-emerald-700" />
                <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">GST Collected</p>
              </div>
              <p className="text-3xl font-bold text-emerald-700">{INR(gstCollected)}</p>
              <p className="text-xs text-slate-500 mt-1">From confirmed client invoices</p>
            </Card>
            <Card className="p-5 bg-amber-50/50 border border-amber-100" data-testid="gst-paid-card">
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee className="h-4 w-4 text-amber-700" />
                <p className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">GST Paid</p>
              </div>
              <p className="text-3xl font-bold text-amber-700">{INR(gstPaid)}</p>
              <p className="text-xs text-slate-500 mt-1">From vendor expenses</p>
            </Card>
            <Card className={`p-5 ${netGst >= 0 ? 'bg-rose-50/50 border-rose-100' : 'bg-teal-50/50 border-teal-100'} border`} data-testid="gst-net-card">
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee className={`h-4 w-4 ${netGst >= 0 ? 'text-rose-700' : 'text-teal-700'}`} />
                <p className={`text-[11px] uppercase tracking-wider font-semibold ${netGst >= 0 ? 'text-rose-700' : 'text-teal-700'}`}>Net GST Liability</p>
              </div>
              <p className={`text-3xl font-bold ${netGst >= 0 ? 'text-rose-700' : 'text-teal-700'}`}>{INR(Math.abs(netGst))}</p>
              <p className="text-xs text-slate-500 mt-1">{netGst >= 0 ? 'Payable to GST authority' : 'Input tax credit'}</p>
            </Card>
          </div>
          <Card className="p-5 bg-white" data-testid="gst-formula">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">How this is calculated</h4>
            <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
              <li><strong>GST Collected</strong> — sum of <code>gstAmount</code> across every confirmed/accepted/completed trip.</li>
              <li><strong>GST Paid</strong> — sum of <code>gstAmount</code> across every expense recorded in the accounting module.</li>
              <li><strong>Net GST Liability</strong> = Collected − Paid. Positive amount is payable to the tax authority; negative is input tax credit you can carry forward.</li>
            </ul>
          </Card>
        </TabsContent>

        {/* TCS sub-tab */}
        <TabsContent value="tcs" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Card className="p-5 bg-violet-50/50 border border-violet-100" data-testid="tcs-collected-card">
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee className="h-4 w-4 text-violet-700" />
                <p className="text-[11px] uppercase tracking-wider text-violet-700 font-semibold">TCS Collected</p>
              </div>
              <p className="text-3xl font-bold text-violet-700">{INR(tcsBreakdown.collected)}</p>
              <p className="text-xs text-slate-500 mt-1">Auto-computed on {tcsBreakdown.count} international trip{tcsBreakdown.count !== 1 ? 's' : ''}</p>
            </Card>
            <Card className="p-5 bg-amber-50/50 border border-amber-100" data-testid="tcs-paid-card">
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee className="h-4 w-4 text-amber-700" />
                <p className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">TCS Paid</p>
              </div>
              <p className="text-3xl font-bold text-amber-700">{INR(tcsPaid)}</p>
              <p className="text-xs text-slate-500 mt-1">From vendor expenses tagged with TCS</p>
            </Card>
          </div>
          <Card className="p-5 bg-white" data-testid="tcs-formula">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">How this is calculated</h4>
            <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
              <li><strong>TCS Collected</strong> — auto-calculated at 2% on every international trip (Section 206C(1G) of the Income Tax Act). Configurable per trip via the <em>TCS Rate</em> field on the Quote.</li>
              <li><strong>TCS Paid</strong> — sum of <code>tcsAmount</code> across every expense recorded in the accounting module.</li>
              <li>TCS Collected must be deposited to the government quarterly; report it on Form 27EQ.</li>
            </ul>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AccountingTaxes;
